import { formatPeso } from '@tahi/core';
import type { EntryHistoryRow } from '../../data/repos/spending';
import { methodLabel } from './format';

export interface HistoryLine {
  id: string;
  at: string;
  title: string;
  details: string[];
}

interface Ctx {
  categoryName(id: string): string;
  sourceName(moduleId: string): string;
  /** Budget (v0.2): a fund's name, for fund moves. */
  fundName?(id: string): string;
  /** v0.5: an account's name, for "From" changes. */
  accountName?(id: string): string;
}

const FIELDS: [string, string][] = [
  ['amountMinor', 'Amount'],
  ['categoryId', 'Category'],
  ['note', 'Note'],
  ['date', 'Date'],
  ['method', 'Paid with'],
  ['fundId', 'Limit'],
  ['accountId', 'From'],
];

function show(field: string, v: unknown, ctx: Ctx): string {
  if (v === null || v === undefined || v === '') return 'none';
  if (field === 'amountMinor') return formatPeso(Number(v));
  if (field === 'categoryId') return ctx.categoryName(String(v));
  if (field === 'method') return methodLabel(String(v));
  if (field === 'fundId') return ctx.fundName ? ctx.fundName(String(v)) : 'a limit';
  if (field === 'accountId') return ctx.accountName ? ctx.accountName(String(v)) : 'an account';
  return String(v);
}

export function describeHistory(rows: readonly EntryHistoryRow[], ctx: Ctx): HistoryLine[] {
  return rows.map((r) => {
    const base = { id: r.id, at: r.at };
    const before = r.before ?? {};
    const after = r.after ?? {};
    if (r.action === 'create') {
      const from =
        r.sourceModule && r.sourceModule !== 'spending'
          ? ` from ${ctx.sourceName(r.sourceModule)}`
          : '';
      const details = after.categoryId
        ? [`Category: ${ctx.categoryName(String(after.categoryId))}`]
        : [];
      return { ...base, title: `Added ${formatPeso(Number(after.amountMinor))}${from}`, details };
    }
    if (r.action === 'delete') return { ...base, title: 'Deleted', details: [] };
    if (
      r.action === 'restore' ||
      ('deletedAt' in after && after.deletedAt === null && before.deletedAt)
    ) {
      return { ...base, title: 'Restored', details: [] };
    }
    const keys = Object.keys(after);
    if (
      r.sourceModule === 'budget' &&
      keys.length === 1 &&
      keys[0] === 'fundId' &&
      !before.fundId
    ) {
      // The budget.deduct rule (v0.2) filling the fund on a new expense.
      return { ...base, title: `Deducted from ${show('fundId', after.fundId, ctx)}`, details: [] };
    }
    const details = FIELDS.filter(([f]) => f in after && after[f] !== before[f]).map(
      ([f, label]) => `${label}: ${show(f, before[f], ctx)} → ${show(f, after[f], ctx)}`,
    );
    return { ...base, title: 'Edited', details };
  });
}
